/**
 * SidebetButton — bottom-center HTML overlay used during a craps round.
 *
 * Three visible states (locked design):
 *   - 'available'  : pulse/glow; tap pre-arms a sidebet for the next boundary.
 *   - 'pre-armed'  : highlighted; tap again to remove the pre-arm.
 *   - 'disabled'   : greyed; the button is consumed by an active SIDEBET phase.
 *
 * Visibility is driven (in GameFlowController.updateCrapsSidebetButton) by
 * server-authoritative state the client actually receives:
 *   - GameUpdate.gameState   — point phases (POINT_ESTABLISHED/POINT_ROLL/
 *     POINT_RESULT) make it available; SIDEBET_BETTING disables it.
 *   - customState.point      : number|null — gates availability on a point
 *     actually being set (rides getGameUpdateExtras.customState).
 *   - customState.sidebetPreArmed : string[] — playerIds currently pre-armed,
 *     drives the 'pre-armed' highlight.
 * (We do NOT read a sidebetButtonAvailable flag — the engine customState map
 * is clobbered by the strategy's getGameUpdateExtras customState pre-broadcast.)
 *
 * Wires `click` → onPress() callback (caller passes a closure that emits
 * the network event). The component is purely visual + click handling; it
 * has no knowledge of game state beyond what setState supplies.
 */

export type SidebetButtonStatus = 'hidden' | 'available' | 'pre-armed' | 'disabled';

export interface SidebetButtonOptions {
  /** Container the button mounts into. Defaults to document.body. */
  container?: HTMLElement;
  /** Click callback. Fires only when status is 'available' or 'pre-armed'. */
  onPress: () => void;
}

export class SidebetButton {
  private root: HTMLButtonElement;
  private labelEl: HTMLElement;
  private status: SidebetButtonStatus = 'hidden';
  private onPress: () => void;

  constructor(opts: SidebetButtonOptions) {
    SidebetButton.injectStyles();

    this.onPress = opts.onPress;
    this.root = document.createElement('button');
    this.root.type = 'button';
    this.root.className = 'sdl-sidebet-button';
    this.root.style.display = 'none';

    this.labelEl = document.createElement('span');
    this.labelEl.className = 'sdl-sidebet-button__label';
    this.labelEl.textContent = 'SIDEBET';
    this.root.appendChild(this.labelEl);

    this.root.addEventListener('click', () => {
      if (this.status === 'available' || this.status === 'pre-armed') {
        this.onPress();
      }
    });

    (opts.container ?? document.body).appendChild(this.root);
  }

  setStatus(status: SidebetButtonStatus): void {
    if (status === this.status) return;
    this.status = status;
    this.root.classList.remove(
      'sdl-sidebet-button--available',
      'sdl-sidebet-button--pre-armed',
      'sdl-sidebet-button--disabled',
    );
    if (status === 'hidden') {
      this.root.style.display = 'none';
      return;
    }
    this.root.style.display = '';
    this.root.classList.add(`sdl-sidebet-button--${status}`);
  }

  destroy(): void {
    this.root.remove();
  }

  private static stylesInjected = false;
  private static injectStyles(): void {
    if (SidebetButton.stylesInjected) return;
    SidebetButton.stylesInjected = true;
    const style = document.createElement('style');
    style.textContent = `
      .sdl-sidebet-button {
        position: fixed;
        /* Bottom-right round chip. During shooting there's no wallet here, and
         * the centre is left clear so it never overlays the local player's
         * sidebet icons (the old bottom-centre placement did). */
        right: 20px;
        bottom: 20px;
        z-index: 50;
        width: 78px;
        height: 78px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        font-family: 'Bebas Neue', 'Rajdhani', sans-serif;
        /* Single line — sized to fit "SIDEBET" across the round face. */
        font-size: 19px;
        line-height: 1;
        letter-spacing: 0.02em;
        white-space: nowrap;
        text-align: center;
        color: #FFD700;
        background: rgba(8, 8, 10, 0.92);
        border: 2px solid #FFD700;
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 0 24px rgba(255, 215, 0, 0.35);
        transition: transform 120ms ease-out, box-shadow 200ms ease-out, background 200ms ease-out, color 200ms ease-out, border-color 200ms ease-out, opacity 200ms ease-out;
        user-select: none;
        -webkit-tap-highlight-color: transparent;
      }
      .sdl-sidebet-button:active {
        transform: scale(0.94);
      }
      .sdl-sidebet-button--available {
        animation: sdl-sidebet-pulse 1.6s ease-in-out infinite;
      }
      .sdl-sidebet-button--pre-armed {
        background: #FFD700;
        color: #08080A;
        box-shadow: 0 0 32px rgba(255, 215, 0, 0.65), inset 0 0 0 2px rgba(0, 0, 0, 0.35);
      }
      .sdl-sidebet-button--disabled {
        color: rgba(255, 215, 0, 0.35);
        border-color: rgba(255, 215, 0, 0.25);
        box-shadow: none;
        opacity: 0.6;
        cursor: default;
      }
      @keyframes sdl-sidebet-pulse {
        0%, 100% { box-shadow: 0 0 24px rgba(255, 215, 0, 0.35); }
        50%      { box-shadow: 0 0 36px rgba(255, 215, 0, 0.65); }
      }
    `;
    document.head.appendChild(style);
  }
}
